import React, {useCallback, useEffect, useMemo, useState} from "react"
import {Button, Card, Col, Descriptions, Modal, Row, Select, Table, Tag} from "antd"
import {Helmet} from "react-helmet-async"
import {useDispatch, useSelector} from "react-redux"
import {NavLink, useHistory, useLocation, useParams} from "react-router-dom"
import {StateType} from "../../store/store"
import {BackButton} from "../../components/BackButton/BackButton"
import {Loading} from "../../components/Loading/Loading"
import moment from "moment/moment"
import {center} from "../../helps/tableHelper"
import {filterToString} from "../../helps/filterToString"
import {isActiveOptionsSelect, limitOptionsSelect} from "../../helps/optionsSelect"
import {filterFromString} from "../../helps/filterFromString"
import deepEqual from "deep-equal"
import {FormSelect} from "../../components/Form/FormSelect"
import {deleteUserGroup, getUserGroup, setUserGroup, setUserGroupSideFilter, setWSUserGroup} from "../../store/userGroupReducer"
import {getUsers, setUsers, setWSUser} from "../../store/userReducer"
import {ID_SYSTEM_COMPANY} from "../../constants/constants"
import {deleteCompany} from "../../store/companyReducer"
import {getObject} from "../../store/objectReducer"
import {FormInput} from "../../components/Form/FormInput"

export const UserGroup = () => {
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()
    const id = parseInt(useParams<{ id: string }>()?.id, 10)
    const status = useSelector((state: StateType) => state.appReducer.status)
    const profile = useSelector((state: StateType) => state.appReducer.profile)
    const userGroup = useSelector((state: StateType) => state.userGroupReducer.userGroup)
    const filter = useSelector((state: StateType) => state.userGroupReducer.sideFilter)
    const users = useSelector((state: StateType) => state.userReducer.users)
    const ws = useSelector((state: StateType) => state.userGroupReducer.ws)
    const wsUser = useSelector((state: StateType) => state.userReducer.ws)
    const [showModal, setShowModal] = useState(false)
    const controller = useMemo(() => new AbortController(), [id])

    const columns = useMemo(() => {
        const array: any = [
            {
                title: 'ФИО',
                dataIndex: 'name',
                key: 'name',
                align: center,
            },
            {
                title: 'Статус',
                dataIndex: 'active',
                key: 'active',
                render: (active: boolean) => {
                    return (
                        <Tag color={active ? 'green' : 'volcano'} style={{marginRight: 0}}>{active ? 'Активно' : 'Не активно'}</Tag>
                    )
                },
                align: center,
            },
        ]

        return array
    }, [userGroup])

    useEffect(() => {
        const filterString = filterFromString(location.search)
        if (filterString && !deepEqual(filterString, filter)) {
            dispatch(setUserGroupSideFilter({...filterString}))
        } else if (location.search !== filterToString(filter)) {
            history.replace({search: filterToString(filter)})
        }
    }, [dispatch, filter, history, location])

    useEffect(() => {
        if (id) dispatch(getUserGroup({id, controller}))

        return () => {
            controller.abort()
            dispatch(setUserGroup(null))
        }
    }, [dispatch, id, controller])

    const getUsersFunc = useCallback((withoutLoadingIndicator = false) => {
        if (id && location?.search) dispatch(getUsers({idUserGroup: id, ...filterFromString(location.search), controller, withoutLoadingIndicator}))
    }, [dispatch, id, location, controller])

    useEffect(() => {
        getUsersFunc()

        return () => {
            dispatch(setUsers(null))
        }
    }, [dispatch, getUsersFunc])

    useEffect(() => {
        if (ws && ws.id === id) {
            dispatch(getUserGroup({id, controller, withoutLoadingIndicator: true}))
        }
        dispatch(setWSUserGroup(null))
    }, [dispatch, ws, id, controller])

    useEffect(() => {
        if (wsUser && wsUser.idParent === id) {
            getUsersFunc(true)
        }
        dispatch(setWSUser(null))
    }, [dispatch, id, wsUser, getUsersFunc])

    const deleteHandler = useCallback(async () =>
    {
        if (userGroup?.id)
        {
            const {payload}: any = await dispatch(deleteUserGroup(userGroup.id))
            if (payload?.status === 0)
            {
                setShowModal(false)
                if (payload?.message === "Удалено") history.goBack()
                else dispatch(getUserGroup({id, controller}))
            }
        }
    }, [userGroup, history, id, controller])

    const data = useMemo(() => users?.data?.map(i => {
        const row: any = {
            key: i.id,
            id: i.id,
            name: i.name,
            active: i.active,
        }

        return row
    }), [users])

    if (!userGroup) return <Loading/>

    return (
        <div className="description-page">
            <Helmet>
                <title>{userGroup?.name}</title>
            </Helmet>
            <div className="description-header">
                <BackButton onClick={() => history.goBack()}/>
                <div>
                    <Button type="link" onClick={() => history.push(`/userGroupForm`, {userGroup})}>Редактировать</Button>
                    <Button type="link" danger onClick={() => setShowModal(true)}>Удалить</Button>
                </div>
            </div>

            <Descriptions bordered column={{xs: 1, sm: 1, md: 2}}>
                <Descriptions.Item label="Название">{userGroup?.name}</Descriptions.Item>
                <Descriptions.Item label="ID">{userGroup?.id}</Descriptions.Item>
                {
                    profile?.idCompany === ID_SYSTEM_COMPANY
                        ? <Descriptions.Item label="Компания">
                            <NavLink to={`/company/${userGroup.idCompany}`}>{userGroup.nameCompany}</NavLink>
                        </Descriptions.Item>
                        : null
                }
                <Descriptions.Item label="Создана">{moment(userGroup?.createdAt).format('DD.MM.YYYY HH:mm')}</Descriptions.Item>
                <Descriptions.Item label="Обновлена">{moment(userGroup?.updatedAt).format('DD.MM.YYYY HH:mm')}</Descriptions.Item>
                <Descriptions.Item label="Статус">
                    <div className="description-item-status">
                        <Tag color={userGroup.active ? 'green' : 'volcano'} style={{marginRight: 0}}>{userGroup.active ? 'Активно' : 'Не активно'}</Tag>
                    </div>
                </Descriptions.Item>
            </Descriptions>

            <div className="tabled-side">
                <Row gutter={[24, 0]}>
                    <Col xs={24}>
                        <Card variant="borderless"
                              className="criclebox tablespace mb-24"
                              title={
                                  <div className="table-title">
                                      <div>Пользователи</div>
                                      <Button onClick={() => history.push('/userForm', {idCompany: userGroup.idCompany, idUserGroup: id})} type="link" size="middle" style={{marginTop: 5}}>
                                          Добавить
                                      </Button>
                                  </div>
                              }
                              extra={
                                  <div className="table-title-extra">
                                      <FormInput placeholder="Поиск"
                                                 containerStyle={{marginBottom: 0, width: "auto", minWidth: 100}}
                                                 value={filter?.search}
                                                 name="search"
                                                 onChange={(e) => history.replace({search: filterToString({...filter, search: e.target.value, offset: 0})})}
                                                 allowClear
                                      />

                                      <FormSelect value={filter?.status}
                                                  containerStyle={{marginBottom: 0, width: 'auto', minWidth: 100}}
                                                  dropdownStyle={{minWidth: 'min-content'}}
                                                  onChange={(value) => history.replace({search: filterToString({...filter, status: value, offset: 0})})}
                                                  options={isActiveOptionsSelect}
                                                  placeholder="Статус"
                                                  allowClear
                                                  placement="bottomRight"
                                      />

                                      <FormSelect value={filter?.limit}
                                                  containerStyle={{marginBottom: 0, width: 'auto'}}
                                                  dropdownStyle={{minWidth: 'min-content'}}
                                                  onChange={(value) => history.replace({search: filterToString({...filter, limit: value, offset: 0})})}
                                                  options={limitOptionsSelect}
                                                  placement="bottomRight"
                                      />
                                  </div>
                              }
                        >
                            <div className="table-responsive">
                                {
                                    users
                                        ? <Table columns={columns}
                                                 dataSource={data}
                                                 pagination={filter.limit ? {
                                                     position: users?.data?.length >= 10 ? ['bottomLeft', 'topLeft'] : ['bottomLeft'],
                                                     pageSize: filter.limit,
                                                     hideOnSinglePage: true,
                                                     total: users.total,
                                                     current: filter?.offset / filter?.limit + 1,
                                                     onChange: (page, pageSize) => {
                                                         const obj = {
                                                             limit: pageSize,
                                                             offset: (page - 1) * pageSize,
                                                         }
                                                         history.replace({search: filterToString({...filter, ...obj})})
                                                     },
                                                     showSizeChanger: false,
                                                 } : false}
                                                 className="ant-border-space"
                                                 onRow={({id}) => ({
                                                     onClick: () => history.push(`/user/${id}`),
                                                     onMouseDown: (e) => e.button === 1 && window.open(`/user/${id}`, '_blank', 'noopener,noreferrer')
                                                 })}
                                                 rowClassName={({deleted}) => deleted ? 'table-row-deleted' : ''}
                                        />
                                        : <Loading/>
                                }
                            </div>
                        </Card>
                    </Col>
                </Row>
            </div>

            <Modal title={`Вы уверены что хотите удалить группу`}
                   centered
                   open={showModal}
                   onOk={deleteHandler}
                   onCancel={() => setShowModal(false)}
                   okText="Да"
                   okButtonProps={{loading: status === "loading"}}
            />
        </div>
    )
}